import { defineAsyncComponent } from 'vue';
import { loadErpDefinitions } from '@/definitions/load';

// ERP's eager slice for the Connect shell (`@erp/connect/shell`, I-1786). The shell
// imports it at startup, before anyone opens ERP: the ERP permission snapshot gates
// the app launcher, the navbar and the command palette of every app. Keep it small —
// everything else sits behind a dynamic import.

export { usePermissionStore } from '@/stores/permissions';
export type { PermissionMode, PermissionRequirement } from '@/permissions';

/**
 * The ERP's screen definitions (BLP/nx#3257): the shell starts the load for the
 * command palette, registers the sink that merges the served texts into the
 * ERP's message table, and Connect loads them before the ERP module evaluates.
 * Store and loader import `apiClient` only.
 */
export { loadErpDefinitions, setErpDefinitionTextSink } from '@/definitions/load';
export { erpDefinitionsLoaded } from '@/definitions/store';
/** The list and record routes (name + path) of a resource, read from the served route table. */
export { erpListRoute, erpRecordRoute } from '@/definitions/routeTable';

/**
 * A lazy ERP module the shell reaches before the ERP is opened, and whose
 * module graph reads the definitions at evaluation, is imported only once they
 * are in: a module that throws there stays broken until a reload. A failed
 * load rejects BEFORE the import, so the next try starts clean.
 */
const afterDefinitions =
  <T>(load: () => Promise<T>) =>
  (): Promise<T> =>
    loadErpDefinitions().then(load);

/**
 * B-DMS deep links: mention and notification targets, the legacy
 * /bdms/correspondences/:id route. Not gated on the definitions: most links
 * (chat, mail, a plain ERP path) need none, and the module reads the route
 * table only inside the functions that resolve an ERP host — those wait for
 * the definitions themselves.
 */
export const loadDeepLinks = () => import('@/modules/bdms/deepLink');

/** ERP's request and transport switches for the shell's developer settings. */
export const loadRequestSettings = () => import('./requestSettings');

/** Layout tooling the shell's settings page embeds. */
export const RegistryLayoutBuilder = defineAsyncComponent(
  afterDefinitions(() => import('@/components/devtools/RegistryLayoutBuilder.vue'))
);
export const BackingLayoutsPanel = defineAsyncComponent(
  afterDefinitions(() => import('@/components/devtools/BackingLayoutsPanel.vue'))
);

/**
 * The command palette's app-wide ERP entries as plain data (I-2348): one per
 * Stammdaten cockpit tile, purchase list and Sales create action.
 * The shell builds the commands (connect/src/integration/erp.ts) so the palette
 * offers them before ERP is opened. These modules hold data only — no views.
 * All are functions: the tiles and document list paths are part of the served
 * definitions, so they answer nothing until those are loaded and read the
 * reactive `erpDefinitionsLoaded` (a computed or watcher re-evaluates then).
 */
export { masterdataCommandTargets } from '@/modules/masterdata/cockpit/tileTargets';
export { salesCreateCommandTargets } from '@/modules/sales/composables/salesCreateTargets';
export { purchaseCommandTargets } from '@/modules/purchase/document/kinds';

/**
 * The ERP record picker the shell provides to every app (`ErpRecordPickerKey`,
 * I-3178): the handle is eager, the dialog — the ERP's `EntityPicker` — loads
 * when it opens.
 */
export { createErpRecordPicker } from './recordPicker/createErpRecordPicker';
export type { ErpRecordPickerHandle } from './recordPicker/createErpRecordPicker';
export const ErpRecordPickerDialog = defineAsyncComponent(
  afterDefinitions(() => import('./recordPicker/ErpRecordPickerDialog.vue'))
);
